/**
 * FriendStatsBanner Component
 * White card with HEAD TO HEAD stats: wins, points, and pick percentages.
 * Numbers count up and % bars fill on load.
 */

import React, { useMemo, useEffect, useState, useRef } from 'react';
import { View, Text, Animated, Easing } from 'react-native';
import { Trophy, Star, Percent } from 'lucide-react-native';
import type { FriendStatsSummary } from '../../../src/friends/types/friends.types';
import type { BothHitRates } from '../../../src/friends/helpers/friendStatsHitRate.helper';
import { HEAD_TO_HEAD_TOOLTIP } from '../../../constants/tooltips';
import { getFriendStatsBannerStyles } from './FriendStatsBanner.styles';
import { useTheme } from '../../../src/theme/useTheme';
import { InfoTooltip } from '../../../src/shared/components/InfoTooltip';

const STATS_ANIM_DURATION_MS = 1200;
const EASE_OUT = Easing.bezier(0.25, 0.1, 0.25, 1);
/** Backend points >= this are stored as cents (e.g. 253 = 2.53). */
const POINTS_CENTS_THRESHOLD = 100;

/** Banner-level guard: survives remount (e.g. Strict Mode) so we only animate once per friend even if parent doesn't re-render. */
const bannerHasAnimatedForFriend = new Set<string>();

interface FriendStatsBannerProps {
  summary: FriendStatsSummary;
  hitRate?: BothHitRates | null;
  /** When false, show final values immediately (e.g. on refresh). When true, run count-up animation (e.g. first load). */
  shouldAnimate?: boolean;
  testID?: string;
}

function useCountUp(
  target: number,
  isInteger: boolean,
  shouldAnimate: boolean,
  deps: unknown[]
): number {
  const [display, setDisplay] = useState(0);
  const startRef = useRef<number | null>(null);
  const frameRef = useRef<number>(0);
  const depsKeyRef = useRef<string>('');

  useEffect(() => {
    if (!shouldAnimate) {
      setDisplay(isInteger ? Math.round(target) : target);
      return;
    }
    const depsKey = deps.map(String).join(',');
    if (depsKeyRef.current === depsKey) {
      return;
    }
    depsKeyRef.current = depsKey;
    startRef.current = null;
    setDisplay(0);
    const duration = STATS_ANIM_DURATION_MS;
    const start = () => {
      const now = Date.now();
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = EASE_OUT(t);
      const value = eased * target;
      setDisplay(isInteger ? Math.round(value) : value);
      if (t < 1) frameRef.current = requestAnimationFrame(start);
    };
    frameRef.current = requestAnimationFrame(start);
    return () => cancelAnimationFrame(frameRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps is the dependency array from caller; target/isInteger are used inside
  }, [shouldAnimate, ...deps]);

  if (!shouldAnimate) {
    return isInteger ? Math.round(target) : target;
  }
  return display;
}

/** Format animated points for display: 2 decimals when target is cents (>= threshold), else integer. */
function formatAnimatedPoints(displayValue: number, targetPoints: number | null | undefined): string {
  if (targetPoints == null) return '0.00';
  if (targetPoints >= POINTS_CENTS_THRESHOLD) return (displayValue / 100).toFixed(2);
  return String(Math.round(displayValue));
}

export const FriendStatsBanner: React.FC<FriendStatsBannerProps> = ({
  summary,
  hitRate,
  shouldAnimate = true,
  testID,
}) => {
  const themeRes = useTheme();
  const styles = useMemo(() => getFriendStatsBannerStyles(themeRes), [themeRes]);
  const friendId = summary?.friend?.id ?? '';
  const alreadyAnimatedBanner = friendId ? bannerHasAnimatedForFriend.has(friendId) : false;
  const effectiveShouldAnimate = Boolean(shouldAnimate && friendId && !alreadyAnimatedBanner);
  if (effectiveShouldAnimate && friendId) {
    bannerHasAnimatedForFriend.add(friendId);
  }

  const hasAnyBets = hitRate != null && (hitRate.user.total > 0 || hitRate.friend.total > 0);
  const showUserPicks = hitRate != null && hitRate.user.total > 0;
  const showFriendPicks = hitRate != null && hitRate.friend.total > 0;

  const displayUserWins = useCountUp(summary.userWins, true, effectiveShouldAnimate, [summary.userWins]);
  const displayFriendWins = useCountUp(summary.friendWins, true, effectiveShouldAnimate, [summary.friendWins]);
  const displayUserPoints = useCountUp(
    summary.userTotalPoints ?? 0,
    false,
    effectiveShouldAnimate,
    [summary.userTotalPoints]
  );
  const displayFriendPoints = useCountUp(
    summary.friendTotalPoints ?? 0,
    false,
    effectiveShouldAnimate,
    [summary.friendTotalPoints]
  );
  const displayUserPct = useCountUp(
    showUserPicks ? hitRate!.user.percentage : 0,
    true,
    effectiveShouldAnimate,
    [hitRate?.user.percentage, showUserPicks]
  );
  const displayFriendPct = useCountUp(
    showFriendPicks ? hitRate!.friend.percentage : 0,
    true,
    effectiveShouldAnimate,
    [hitRate?.friend.percentage, showFriendPicks]
  );

  const barUser = useRef(new Animated.Value(0)).current;
  const barFriend = useRef(new Animated.Value(0)).current;
  const lastBarTargets = useRef<{ user: number; friend: number } | null>(null);

  useEffect(() => {
    const toUser = showUserPicks ? hitRate!.user.percentage : 0;
    const toFriend = showFriendPicks ? hitRate!.friend.percentage : 0;
    const same = lastBarTargets.current?.user === toUser && lastBarTargets.current?.friend === toFriend;
    if (same) return;
    lastBarTargets.current = { user: toUser, friend: toFriend };
    if (effectiveShouldAnimate && (toUser > 0 || toFriend > 0)) {
      barUser.setValue(0);
      barFriend.setValue(0);
      Animated.parallel([
        Animated.timing(barUser, {
          toValue: toUser,
          duration: STATS_ANIM_DURATION_MS,
          easing: EASE_OUT,
          useNativeDriver: false,
        }),
        Animated.timing(barFriend, {
          toValue: toFriend,
          duration: STATS_ANIM_DURATION_MS,
          easing: EASE_OUT,
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      barUser.setValue(toUser);
      barFriend.setValue(toFriend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit hitRate (object ref); we use hitRate?.user.percentage and hitRate?.friend.percentage
  }, [effectiveShouldAnimate, barUser, barFriend, showUserPicks, showFriendPicks, hitRate?.user.percentage, hitRate?.friend.percentage]);

  const userBarWidth = barUser.interpolate({
    inputRange: [0, 100],
    outputRange: ['0%', '100%'],
  });
  const friendBarWidth = barFriend.interpolate({
    inputRange: [0, 100],
    outputRange: ['0%', '100%'],
  });

  return (
    <View style={styles.h2hCard} testID={testID || 'friend-stats-banner'}>
      <View style={styles.h2hHeaderRow}>
        <Text style={styles.h2hTitle}>Head to Head</Text>
        <InfoTooltip
          content={HEAD_TO_HEAD_TOOLTIP}
          accessibilityLabel="What is Head to Head?"
          compact
        />
      </View>

      <View style={styles.h2hStatRow}>
        <View style={styles.h2hStatCell}>
          <Text style={styles.h2hStatValueWins}>{Math.round(displayUserWins)}</Text>
          <Text style={styles.h2hStatLabel}>Wins</Text>
        </View>
        <View style={styles.h2hIconCenterGold}>
          <Trophy size={20} color="#F6AD55" />
        </View>
        <View style={styles.h2hStatCell}>
          <Text style={styles.h2hStatValueWins}>{Math.round(displayFriendWins)}</Text>
          <Text style={styles.h2hStatLabel}>Wins</Text>
        </View>
      </View>

      <View style={styles.h2hStatRow}>
        <View style={styles.h2hStatCell}>
          <Text style={styles.h2hStatValueUser}>
            {formatAnimatedPoints(displayUserPoints, summary.userTotalPoints)}
          </Text>
          <Text style={styles.h2hStatLabel}>Pts</Text>
        </View>
        <View style={styles.h2hIconCenterStar}>
          <Star size={20} color={themeRes.primary} />
        </View>
        <View style={styles.h2hStatCell}>
          <Text style={styles.h2hStatValueFriend}>
            {formatAnimatedPoints(displayFriendPoints, summary.friendTotalPoints)}
          </Text>
          <Text style={styles.h2hStatLabel}>Pts</Text>
        </View>
      </View>

      <View style={[styles.h2hStatRow, styles.h2hStatRowLast]}>
        <View style={styles.h2hBarCell}>
          <View style={styles.h2hBarBg}>
            <Animated.View
              style={[styles.h2hBarFillUser, { width: userBarWidth }]}
            />
          </View>
          <Text style={styles.h2hBarLabel}>
            {showUserPicks
              ? `${hitRate!.user.hits}/${hitRate!.user.total} (${displayUserPct}%)`
              : hasAnyBets
                ? '0/0 (0%)'
                : '—'}
          </Text>
        </View>
        <View style={styles.h2hIconCenter}>
          <Percent size={18} color={themeRes.gray[500]} />
        </View>
        <View style={styles.h2hBarCell}>
          <View style={styles.h2hBarBg}>
            <Animated.View
              style={[styles.h2hBarFillFriend, { width: friendBarWidth }]}
            />
          </View>
          <Text style={styles.h2hBarLabel}>
            {showFriendPicks
              ? `${hitRate!.friend.hits}/${hitRate!.friend.total} (${displayFriendPct}%)`
              : hasAnyBets
                ? '0/0 (0%)'
                : '—'}
          </Text>
        </View>
      </View>
    </View>
  );
};
